// Which sound a change of the review's state deserves (2026-10-15). The
// listener compares the position on the board before and after: the same
// position is silent (a scan arriving, a shortlist reading); one move forward
// along the path the board travels (a step, a dragged piece, the scoresheet's
// click making its move, the main line or a solution playing out, each ply
// its own state) sounds as that move, a capture, a check or castling by its
// flags; one move back sounds as a plain move; anything else that changes the
// position in one state (Next mistake, a scoresheet click's first step, the
// take-back of a wrong puzzle move) is a jump, one dull thud whatever the
// distance. Pure, tested. The puzzle's chimes come from its events, not from
// here.
import type { Fen, Move } from '@chess/api-model';
import { lastMoveAt, positionAt, viewPly, type ReviewState } from '@chess/api-review';

export type Sound = 'move' | 'capture' | 'check' | 'castle' | 'jump' | 'solved' | 'failed';

/** The position at a ply along the path the board shows: the game's up to the line's start, then the line's (the review API's own rule). */
function pathPosition(state: ReviewState, ply: number): Fen | undefined {
  if (ply < 0) return undefined;
  const line = state.exploring;
  if (line && ply > line.fromPly) return line.positions[ply - line.fromPly];
  return state.game?.positions[ply];
}

/** The sound of a move by what it did: a check (or mate) over castling over a capture over a plain move. */
export function soundOfMove(move: Move): Sound {
  if (move.flags.check || move.flags.checkmate) return 'check';
  if (move.flags.castle) return 'castle';
  if (move.flags.capture || move.flags.enPassant) return 'capture';
  return 'move';
}

/** The sound for the board going from one state to the next, or none when the position stayed, no game was open, or the game changed. */
export function soundFor(prev: ReviewState | undefined, next: ReviewState): Sound | undefined {
  const to = positionAt(next);
  if (!to || !prev || prev.gameId !== next.gameId) return undefined;
  const from = positionAt(prev);
  if (!from || from === to) return undefined;
  const was = viewPly(prev);
  const now = viewPly(next);
  if (now === was + 1 && pathPosition(next, was) === from) {
    const move = lastMoveAt(next);
    return move ? soundOfMove(move) : 'move';
  }
  if (now === was - 1 && pathPosition(prev, now) === to) return 'move';
  return 'jump';
}
